import React, { useState, useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';

import { useForm, FormProvider } from 'react-hook-form';

import { AuthContext } from 'components/authorization/AuthProvider';

import { Typography } from '@mui/material';

import {
  ButtonStyled,
  DialogContentStyled,
  DialogActionsStyled,
  Error,
} from 'elements/mismosh';
import { AuthAlternative, LinkStyled } from './styles';
import { TextFieldFormStyled } from 'elements/formTextField';

import { ContentToggler } from './Container';

type FormData = { code: string };

interface Props {
  switchToClose: ContentToggler;
  handleSuccess: ContentToggler;
  attribute: 'email' | 'phone';
  resender: (username: string) => Promise<void>;
  submitter: (code: string, username: string) => Promise<void>;
}

export const VerificationCode: React.FC<Props> = ({
  switchToClose,
  handleSuccess,
  attribute,
  resender,
  submitter,
}) => {
  const [apiError, setApiError] = useState('');
  const { authUser } = useContext(AuthContext);

  const methods = useForm<FormData>({
    defaultValues: { code: '' },
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const title = `${attribute.charAt(0).toUpperCase()}${attribute.slice(
    1,
  )} Verification Code`;

  const handleClose = () => {
    switchToClose();
  };

  const onSubmit = async ({ code }: FormData, e: React.SyntheticEvent) => {
    e.preventDefault();
    submitter(code, authUser.username)
      .then(() => {
        setApiError(undefined);
        handleSuccess();
      })
      .catch((err) => {
        const message = err?.message;
        if (message?.indexOf('current status is CONFIRMED')) {
          // This is an odd case where the user types in junk just to contine
          // but they are already confirmed.  Most likely caused by admin using
          // the CLI or otherwise to out of band force verify the #.
          handleSuccess();
        }
        setApiError(err?.message);
        console.log('error...: ', err);
      });
  };

  async function handleResend() {
    try {
      await resender(authUser.username);
      setApiError(undefined);
    } catch (err) {
      setApiError(err?.message);
      console.log('error...: ', err);
    }
  }

  const { ref: refCode, ...restCode } = register('code', { required: true });

  return (
    <Dialog open={true} onClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle id="signup-modal">{title}</DialogTitle>
          <DialogContentStyled>
            <TextFieldFormStyled
              {...restCode}
              inputRef={refCode}
              label="Code"
            />
            {errors.code && <Error>Field is required</Error>}

            {!!apiError && <Error>{apiError}</Error>}
          </DialogContentStyled>

          <DialogActionsStyled>
            <ButtonStyled variant="contained" color="primary" type="submit">
              Submit
            </ButtonStyled>
          </DialogActionsStyled>
          <AuthAlternative>
            <Typography variant="caption">Need another code?</Typography>
            <LinkStyled onClick={() => handleResend()}>Resend Code</LinkStyled>
          </AuthAlternative>
        </form>
      </FormProvider>
    </Dialog>
  );
};
